import { type ColumnHelper } from "@tanstack/react-table";

export interface CreateOneLineColumnOptions {
  cellClasses?: string;
  headerClasses?: string;
  cellRenderer?: (value: unknown) => string | number | null | undefined;
}

/**
 * Creates a factory function for one-line table columns with consistent styling.
 * This factory can be used with any table type by passing the appropriate columnHelper.
 *
 * @example
 * ```tsx
 * const columnHelper = createColumnHelper<MyType>();
 * const createOneLineColumn = createOneLineColumnFactory(columnHelper);
 *
 * const columns = [
 *   createOneLineColumn("name", "Name"),
 *   createOneLineColumn("date", "Date", {
 *     cellRenderer: (value) => formatDateString(value as Date, "LLL")
 *   }),
 * ];
 * ```
 *
 * @param columnHelper - The column helper from TanStack Table (created with createColumnHelper)
 * @returns A function to create one-line columns with consistent styling
 */
export function createOneLineColumnFactory<T extends object>(
  columnHelper: ColumnHelper<T>
) {
  return (
    accessor: keyof T | string,
    headerText: string,
    options?: CreateOneLineColumnOptions
  ) => {
    const {
      cellClasses = "whitespace-nowrap px-1",
      headerClasses = "px-1",
      cellRenderer,
    } = options || {};

    return columnHelper.accessor(accessor as any, {
      cell: info => {
        const value = info.getValue();
        const displayValue = cellRenderer ? cellRenderer(value) : value;
        return (
          <span className={cellClasses}>
            {displayValue != null ? String(displayValue) : ""}
          </span>
        );
      },
      header: () => <span className={headerClasses}>{headerText}</span>,
    });
  };
}
